import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Save, Plus, Trash2 } from "lucide-react";
import DropZone from "../components/DropZone";

function resolveSrc(u) { if (!u) return ""; if (u.startsWith("http")) return u; if (u.startsWith("/api/media/")) return `${process.env.REACT_APP_BACKEND_URL}${u}`; return u; }

const DEFAULT_SOBRE = {
  hero_eyebrow: "Sobre · The Real Estate Legacy",
  hero_title_html: "Um legado <em>imobiliário</em>.",
  hero_subtitle: "Construído por mais de três décadas ao lado das maiores incorporadoras do Brasil — com método próprio, time multidisciplinar e parceria oficial Google.",
  hero_video_poster: "https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600",
  hero_video_url: "",
  manifesto_title_html: "O imóvel na planta é, antes de tudo, <span class=\"text-accent-orange\">sonho</span>, projeção e percepção de valor.",
  manifesto_paragraph: "Por isso, nosso método combina ciência e arte: dados de mercado para reduzir o risco, e narrativa de marca para gerar o desejo.",
  manifesto_hashtag: "#ApaixonadosPorImobiliario",
  pillars_eyebrow: "Nosso Método",
  pillars_title: "Quatro pilares da Construção de Valor do Lugar.",
  team_title: "O time.",
  team_footnote: "+22 colaboradores em Goiânia, Cuiabá e Brasília.",
  founders_title: "Fundadores",
  reasons_title: "Por que escolher a Places?",
  history_title: "Histórico de projetos.",
  history_subtitle: "Mais de 300 lançamentos em 17 estados. Filtre por ano ou busque por palavra-chave.",
};

let _seuid = 0;
const withKey = (o) => (typeof o === "object" && o !== null ? { ...o, _k: o._k ?? `se${++_seuid}` } : { _k: `se${++_seuid}`, value: o });
const stripKeysP = (arr) => arr.map(({ _k, ...rest }) => rest);
// Reasons is an array of strings — wrap/unwrap for stable keys
const wrapReason = (s) => ({ _k: `sr${++_seuid}`, value: typeof s === "string" ? s : (s?.value || "") });
const unwrapReasons = (arr) => arr.map((r) => (typeof r === "string" ? r : r.value || "")).filter((r) => r && r.trim());

export default function SobreEditor() {
  const [sobre, setSobre] = useState(DEFAULT_SOBRE);
  const [pillars, setPillars] = useState([]);
  const [reasons, setReasons] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/kv/sobre").then(({ data }) => data && setSobre({ ...DEFAULT_SOBRE, ...data }));
    api.get("/content/kv/pillars").then(({ data }) => Array.isArray(data) && setPillars(data.map(withKey)));
    api.get("/content/kv/reasons").then(({ data }) => Array.isArray(data) && setReasons(data.map(wrapReason)));
  }, []);

  const upd = (k, v) => setSobre((s) => ({ ...s, [k]: v }));
  const updPillar = (i, k, v) => setPillars((arr) => { const a = [...arr]; a[i] = { ...a[i], [k]: v }; return a; });
  const addPillar = () => setPillars((arr) => [...arr, withKey({ n: String(arr.length + 1).padStart(2, "0"), color: "#D4FF00", title: "", subtitle: "", text: "" })]);
  const removePillar = (i) => setPillars((arr) => arr.filter((_, j) => j !== i));
  const updReason = (i, v) => setReasons((arr) => { const a = [...arr]; a[i] = { ...a[i], value: v }; return a; });
  const addReason = () => setReasons((arr) => [...arr, wrapReason("")]);
  const removeReason = (i) => setReasons((arr) => arr.filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content/kv/sobre", { value: sobre });
      await api.put("/content/kv/pillars", { value: stripKeysP(pillars) });
      await api.put("/content/kv/reasons", { value: unwrapReasons(reasons) });
      toast.success("Página Sobre atualizada.");
    } catch (e) {
      toast.error("Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  const tf = (k, label, type = "input") => (
    <div>
      <label className="text-xs text-neutral-500">{label}</label>
      {type === "textarea" ? (
        <textarea value={sobre[k] || ""} onChange={(e) => upd(k, e.target.value)} rows={3} className="w-full border border-black/15 px-3 py-2 rounded text-sm" data-testid={`sobre-${k}`} />
      ) : (
        <input value={sobre[k] || ""} onChange={(e) => upd(k, e.target.value)} className="w-full border border-black/15 px-3 py-2 rounded text-sm" data-testid={`sobre-${k}`} />
      )}
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-end mb-6">
        <div>
          <h1 className="font-display text-4xl">Página Sobre</h1>
          <p className="text-sm text-neutral-500 mt-1">Hero, manifesto, 4 pilares, motivos e títulos editáveis.</p>
        </div>
        <button onClick={save} disabled={saving} data-testid="sobre-save" className="bg-black text-white text-sm px-5 py-2.5 rounded-full hover:bg-accent-orange disabled:opacity-50">
          <Save className="w-3 h-3 inline mr-1" /> {saving ? "Salvando…" : "Salvar tudo"}
        </button>
      </div>

      <section className="mb-6 border border-black/10 p-5 rounded space-y-3">
        <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Hero (topo)</div>
        <div className="grid md:grid-cols-2 gap-3">
          {tf("hero_eyebrow", "Eyebrow / pequena legenda")}
          {tf("hero_subtitle", "Subtítulo", "textarea")}
          <div className="md:col-span-2">{tf("hero_title_html", "Título (HTML permitido para <em> e <span>)", "textarea")}</div>
          <div className="md:col-span-2">
            <label className="text-xs text-neutral-500">Imagem de capa do hero (upload ou URL)</label>
            {sobre.hero_video_poster && <img src={resolveSrc(sobre.hero_video_poster)} alt="" className="w-full max-h-48 object-cover rounded my-2" />}
            <DropZone multiple={false} onUploaded={(f) => upd("hero_video_poster", f.url)} />
            <input value={sobre.hero_video_poster || ""} onChange={(e) => upd("hero_video_poster", e.target.value)} placeholder="ou URL" className="w-full text-xs border border-black/15 px-2 py-1.5 rounded mt-1" data-testid="sobre-hero_video_poster" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs text-neutral-500">Vídeo do hero — upload direto (MP4/WebM/MOV, até 100MB) ou URL externa (Vimeo/YouTube)</label>
            {sobre.hero_video_url && sobre.hero_video_url.startsWith("/api/media/") && (
              <video src={resolveSrc(sobre.hero_video_url)} controls className="w-full max-h-48 rounded my-2 bg-black" />
            )}
            <DropZone multiple={false} accept={{ "video/*": [".mp4", ".webm", ".mov", ".m4v"] }} hint="Vídeos · até 100MB" onUploaded={(f) => upd("hero_video_url", f.url)} />
            <input value={sobre.hero_video_url || ""} onChange={(e) => upd("hero_video_url", e.target.value)} placeholder="ou URL do vídeo (Vimeo, YouTube, arquivo)" className="w-full text-sm border border-black/15 px-3 py-2 rounded mt-1" data-testid="sobre-hero_video_url" />
          </div>
        </div>
      </section>

      <section className="mb-6 border border-black/10 p-5 rounded space-y-3">
        <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Manifesto</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2">{tf("manifesto_title_html", "Título (HTML)", "textarea")}</div>
          <div className="md:col-span-2">{tf("manifesto_paragraph", "Parágrafo", "textarea")}</div>
          {tf("manifesto_hashtag", "Hashtag / assinatura")}
        </div>
      </section>

      <section className="mb-6 border border-black/10 p-5 rounded space-y-3">
        <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Títulos das seções</div>
        <div className="grid md:grid-cols-2 gap-3">
          {tf("pillars_eyebrow", "Pilares — Eyebrow")}
          {tf("pillars_title", "Pilares — Título")}
          {tf("team_title", "Equipe — Título")}
          {tf("team_footnote", "Equipe — Rodapé (colaboradores)")}
          {tf("founders_title", "Fundadores — Título")}
          {tf("reasons_title", "Motivos — Título")}
          {tf("history_title", "Histórico — Título")}
          {tf("history_subtitle", "Histórico — Subtítulo", "textarea")}
        </div>
      </section>

      <section className="mb-6 border border-black/10 p-5 rounded">
        <div className="flex justify-between items-center mb-3">
          <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">4 Pilares</div>
          <button onClick={addPillar} className="text-xs bg-accent-yellow px-3 py-1 rounded-full font-semibold" data-testid="sobre-add-pillar"><Plus className="w-3 h-3 inline" /> Adicionar</button>
        </div>
        <div className="space-y-3">
          {pillars.map((p, i) => (
            <div key={p._k || `pillar-${i}`} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-start border border-black/5 p-3 rounded" data-testid={`sobre-pillar-${i}`}>
              <input value={p.n || ""} onChange={(e) => updPillar(i, "n", e.target.value)} placeholder="01" className="md:col-span-1 border border-black/15 px-2 py-1.5 rounded text-sm" />
              <input type="color" value={p.color || "#D4FF00"} onChange={(e) => updPillar(i, "color", e.target.value)} className="md:col-span-1 w-full h-9 border border-black/15 rounded cursor-pointer" />
              <input value={p.title || ""} onChange={(e) => updPillar(i, "title", e.target.value)} placeholder="Título" className="md:col-span-3 border border-black/15 px-2 py-1.5 rounded text-sm" />
              <input value={p.subtitle || ""} onChange={(e) => updPillar(i, "subtitle", e.target.value)} placeholder="Subtítulo" className="md:col-span-3 border border-black/15 px-2 py-1.5 rounded text-sm" />
              <textarea value={p.text || ""} onChange={(e) => updPillar(i, "text", e.target.value)} placeholder="Texto" rows={2} className="md:col-span-3 border border-black/15 px-2 py-1.5 rounded text-sm" />
              <button onClick={() => removePillar(i)} className="md:col-span-1 text-red-500 text-xs justify-self-center"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          ))}
        </div>
      </section>

      <section className="border border-black/10 p-5 rounded">
        <div className="flex justify-between items-center mb-3">
          <div className="font-semibold text-sm uppercase tracking-widest text-neutral-700">Motivos para escolher a Places ({reasons.length})</div>
          <button onClick={addReason} className="text-xs bg-accent-yellow px-3 py-1 rounded-full font-semibold" data-testid="sobre-add-reason"><Plus className="w-3 h-3 inline" /> Adicionar</button>
        </div>
        <div className="space-y-2">
          {reasons.map((r, i) => (
            <div key={r._k || `reason-${i}`} className="grid grid-cols-12 gap-2 items-center" data-testid={`sobre-reason-${i}`}>
              <span className="col-span-1 text-xs text-neutral-400 text-right font-mono">{String(i + 1).padStart(2, "0")}</span>
              <input value={r.value || ""} onChange={(e) => updReason(i, e.target.value)} className="col-span-10 border border-black/15 px-3 py-2 rounded text-sm" />
              <button onClick={() => removeReason(i)} className="col-span-1 text-red-500"><Trash2 className="w-3 h-3 inline" /></button>
            </div>
          ))}
          {!reasons.length && <div className="text-xs text-neutral-500 text-center py-4">Nenhum motivo cadastrado.</div>}
        </div>
      </section>
    </div>
  );
}
